/**
 * ImageAltManager.jsx
 * AI Vision-based ALT text generation for Salla product images.
 * Flow: Scan products → AI generates Arabic ALT (GPT-4o-mini vision) → Approve → Apply to Salla
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Image as ImageIcon, Sparkles, CheckCircle2, X, Pencil, Check,
  Loader2, AlertTriangle, Eye, RefreshCw, Send, ChevronDown, ChevronUp,
} from "lucide-react";

function StatCard({ icon, label, value, hint, color = "blue" }) {
  const tones = {
    blue:   "bg-blue-50 text-[#004EEB]",
    amber:  "bg-amber-50 text-amber-500",
    green:  "bg-emerald-50 text-emerald-500",
    slate:  "bg-slate-50 text-slate-400",
  };
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex items-center gap-3">
      <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${tones[color]}`}>
        {icon}
      </div>
      <div>
        <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-black text-slate-800">{value}</p>
        {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
      </div>
    </div>
  );
}

export default function ImageAltManager({ sallaConnected }) {
  const [inventory, setInventory]   = useState({ products: [], summary: {} });
  const [scanning, setScanning]     = useState(false);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying]     = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [filter, setFilter]         = useState("pending");
  const [selected, setSelected]     = useState(new Set());          // product_ids selected for generation
  const [bulk, setBulk]             = useState(new Set());          // suggestion ids
  const [editing, setEditing]       = useState({});                 // sid → text
  const [expanded, setExpanded]     = useState({});                 // product_id → bool
  const [view, setView]             = useState("inventory");        // inventory | review

  const fetchInventory = async () => {
    try {
      const { data } = await api.get("/seo/images/inventory");
      setInventory(data || { products: [], summary: {} });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "تعذّر جلب جرد الصور";
      toast.error(msg);
    }
  };

  const fetchSuggestions = async (status = filter) => {
    try {
      const { data } = await api.get(`/seo/images/suggestions?status=${status}`);
      setSuggestions(data.suggestions || []);
    } catch {}
  };

  useEffect(() => {
    fetchInventory();
    fetchSuggestions("pending");
    // eslint-disable-next-line
  }, []);

  const scanSelected = async () => {
    if (selected.size === 0) {
      toast.error("حدد منتجاً واحداً على الأقل");
      return;
    }
    if (!sallaConnected) { toast.error("يلزم ربط متجر سلة"); return; }
    setScanning(true);
    try {
      const { data } = await api.post("/seo/images/scan", { product_ids: [...selected] });
      toast.success(`تم فحص ${data.scanned} منتج`);
      fetchInventory();
    } catch (e) { toast.error(e?.response?.data?.detail || "خطأ في الفحص"); }
    finally { setScanning(false); }
  };

  const generateForSelected = async () => {
    if (selected.size === 0) { toast.error("حدد منتجاً واحداً على الأقل"); return; }
    if (!sallaConnected) { toast.error("يلزم ربط متجر سلة"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/seo/images/generate", {
        product_ids: [...selected],
        only_missing: true,
      });
      if (data.created === 0) {
        toast.info("لا توجد صور تحتاج ALT — جميعها مكتمل أو لديها اقتراحات");
      } else {
        toast.success(`تم توليد ${data.created} اقتراح ALT — راجعها للموافقة`);
      }
      setSelected(new Set());
      setView("review");
      fetchSuggestions("pending");
      fetchInventory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "خطأ في التوليد");
    }
    finally { setGenerating(false); }
  };

  const approveOne = async (sid) => {
    try {
      await api.put(`/seo/images/suggestions/${sid}/approve`);
      setSuggestions(s => s.map(x => x.id === sid ? { ...x, status: "approved" } : x));
      toast.success("تمت الموافقة");
    } catch { toast.error("خطأ"); }
  };

  const rejectOne = async (sid) => {
    try {
      await api.put(`/seo/images/suggestions/${sid}/reject`);
      setSuggestions(s => s.map(x => x.id === sid ? { ...x, status: "rejected" } : x));
    } catch { toast.error("خطأ"); }
  };

  const saveEdit = async (sid) => {
    const text = (editing[sid] || "").trim();
    if (!text) return;
    try {
      await api.post(`/seo/images/suggestions/${sid}/edit`, { suggested_alt: text });
      setSuggestions(s => s.map(x => x.id === sid ? { ...x, suggested_alt: text } : x));
      setEditing(e => { const n = { ...e }; delete n[sid]; return n; });
      toast.success("تم التعديل");
    } catch { toast.error("خطأ"); }
  };

  const bulkAction = async (action) => {
    const ids = [...bulk];
    if (ids.length === 0) return;
    try {
      const { data } = await api.post("/seo/images/bulk-action", { suggestion_ids: ids, action });
      toast.success(`تم تحديث ${data.updated} اقتراح`);
      setBulk(new Set());
      fetchSuggestions(filter);
    } catch (e) { toast.error(e?.response?.data?.detail || "خطأ"); }
  };

  const applyApproved = async () => {
    const ids = suggestions.filter(s => s.status === "approved").map(s => s.id);
    if (ids.length === 0) { toast.error("لا توجد اقتراحات معتمدة"); return; }
    setApplying(true);
    try {
      await api.post("/seo/images/apply", { suggestion_ids: ids });
      toast.success("جاري التطبيق على سلة...");
      setTimeout(() => { fetchSuggestions(filter); fetchInventory(); }, 3500);
    } catch (e) { toast.error(e?.response?.data?.detail || "خطأ"); }
    finally { setApplying(false); }
  };

  const products = inventory.products || [];
  const summary  = inventory.summary || {};
  const pending  = suggestions.filter(s => s.status === "pending");
  const approved = suggestions.filter(s => s.status === "approved");

  const productsNeedingAttention = useMemo(() => {
    // Products that have either unknown scan, or missing ALT > 0
    return products.filter(p =>
      p.total_images === null || (p.images_missing_alt || 0) > 0
    );
  }, [products]);

  const toggleSelect = (pid) => {
    setSelected(s => {
      const n = new Set(s);
      n.has(pid) ? n.delete(pid) : n.add(pid);
      return n;
    });
  };

  return (
    <div className="space-y-5" data-testid="image-alt-manager">

      {/* ── Stats ── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatCard
          icon={<ImageIcon className="w-5 h-5" />}
          label="منتجات مفحوصة"
          value={products.length}
          hint={`من إجمالي ${summary.total_products || products.length}`}
          color="blue"
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="صور بدون ALT"
          value={summary.total_images_missing_alt ?? "—"}
          hint="بحاجة لتوليد"
          color="amber"
        />
        <StatCard
          icon={<Sparkles className="w-5 h-5" />}
          label="اقتراحات معلقة"
          value={pending.length}
          hint="بانتظار المراجعة"
          color="blue"
        />
        <StatCard
          icon={<CheckCircle2 className="w-5 h-5" />}
          label="تغطية ALT"
          value={summary.coverage_pct != null ? `${summary.coverage_pct}%` : "—"}
          hint="من صور المتجر"
          color="green"
        />
      </div>

      {/* ── Sub Tabs ── */}
      <div className="flex gap-1 border-b border-slate-100">
        {[
          { id: "inventory", label: "جرد الصور",       icon: <ImageIcon className="w-3.5 h-3.5" /> },
          { id: "review",    label: "مراجعة الاقتراحات", icon: <Eye className="w-3.5 h-3.5" />, badge: pending.length },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            data-testid={`image-alt-tab-${t.id}`}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              view === t.id ? "border-[#004EEB] text-[#004EEB]" : "border-transparent text-slate-400 hover:text-slate-700"
            }`}
          >
            {t.icon}{t.label}
            {t.badge > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#004EEB] text-white text-[9px] font-black rounded-full flex items-center justify-center">
                {t.badge > 9 ? "9+" : t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ══════════════ INVENTORY VIEW ══════════════ */}
      {view === "inventory" && (
        <div className="space-y-4">
          {!sallaConnected && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              يلزم ربط متجر سلة لاستخدام تحسين الصور.
            </div>
          )}

          {products.length === 0 && sallaConnected && (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
              <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 font-medium mb-1">لا يوجد جرد بعد</p>
              <p className="text-xs text-slate-400">شغّل «فحص SEO» أولاً من تبويب فحص SEO</p>
            </div>
          )}

          {products.length > 0 && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => {
                    const all = productsNeedingAttention.map(p => p.product_id);
                    setSelected(new Set(all));
                  }}
                  className="text-sm text-[#004EEB] font-semibold hover:underline"
                  data-testid="select-all-image-products-btn"
                >
                  تحديد كل ما يحتاج تحسين ({productsNeedingAttention.length})
                </button>
                {selected.size > 0 && (
                  <button
                    onClick={() => setSelected(new Set())}
                    className="text-sm text-slate-400 hover:text-slate-600"
                  >
                    إلغاء التحديد
                  </button>
                )}

                {selected.size > 0 && (
                  <>
                    <button
                      onClick={scanSelected}
                      disabled={scanning}
                      data-testid="scan-images-btn"
                      className="flex items-center gap-2 bg-slate-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors"
                    >
                      {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      {scanning ? "جاري الفحص..." : `فحص صور ${selected.size}`}
                    </button>

                    <button
                      onClick={generateForSelected}
                      disabled={generating || !sallaConnected}
                      data-testid="generate-image-alts-btn"
                      className="flex items-center gap-2 bg-amber-500 text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-amber-600 disabled:opacity-50 transition-colors"
                    >
                      {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {generating ? "AI يحلل الصور..." : `توليد ALT لـ ${selected.size} منتج`}
                    </button>
                  </>
                )}
              </div>

              <div className="text-xs text-slate-400 flex items-center gap-1.5">
                <span>النظام يستخدم GPT-4o Vision لتحليل كل صورة وكتابة نص بديل عربي محسّن للسيو.</span>
                <span className="text-emerald-600 font-semibold">صور منتجاتك الأصلية لا تُعدَّل أو تُضغط — فقط نص ALT يُحدَّث.</span>
              </div>

              <div className="space-y-2">
                {productsNeedingAttention.map((p) => {
                  const isSelected = selected.has(p.product_id);
                  const isExpanded = expanded[p.product_id];
                  const isUnknown  = p.total_images == null;
                  return (
                    <div
                      key={p.product_id}
                      className={`border rounded-2xl overflow-hidden transition-all ${
                        isSelected ? "border-blue-300 bg-blue-50" : "border-slate-100 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3 p-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(p.product_id)}
                          className="w-4 h-4 accent-[#004EEB] cursor-pointer"
                          data-testid={`image-product-checkbox-${p.product_id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{p.product_name}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px]">
                            {isUnknown ? (
                              <span className="text-slate-400">لم يُفحص بعد — فعّل «فحص الصور» لمعرفة العدد</span>
                            ) : (
                              <>
                                <span className="text-slate-500">
                                  {p.total_images} صورة •
                                </span>
                                <span className="text-emerald-600 font-semibold">
                                  {p.images_with_alt} لديها ALT
                                </span>
                                <span className="text-amber-600 font-semibold">
                                  {p.images_missing_alt} بدون ALT
                                </span>
                                {p.suggestions?.pending > 0 && (
                                  <span className="text-[#004EEB] font-semibold">
                                    • {p.suggestions.pending} اقتراح معلّق
                                  </span>
                                )}
                                {p.suggestions?.applied > 0 && (
                                  <span className="text-emerald-700 font-semibold">
                                    • {p.suggestions.applied} مُطبَّق
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => setExpanded(e => ({ ...e, [p.product_id]: !isExpanded }))}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ══════════════ REVIEW VIEW ══════════════ */}
      {view === "review" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            {["pending", "approved", "applied", "rejected", "all"].map(s => (
              <button
                key={s}
                onClick={() => { setFilter(s); fetchSuggestions(s); }}
                data-testid={`image-filter-${s}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                  filter === s ? "bg-[#004EEB] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {{pending: "معلّقة", approved: "معتمدة", applied: "مُطبَّقة", rejected: "مرفوضة", all: "الكل"}[s]}
              </button>
            ))}

            <div className="flex-1" />

            {filter === "pending" && pending.length > 0 && (
              <>
                {bulk.size === 0 ? (
                  <button
                    onClick={() => setBulk(new Set(pending.map(p => p.id)))}
                    className="text-xs text-[#004EEB] font-semibold hover:underline"
                  >
                    تحديد الكل ({pending.length})
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => bulkAction("approve")}
                      data-testid="bulk-approve-images-btn"
                      className="flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-emerald-700"
                    >
                      <Check className="w-3 h-3" /> اعتماد ({bulk.size})
                    </button>
                    <button
                      onClick={() => bulkAction("reject")}
                      data-testid="bulk-reject-images-btn"
                      className="flex items-center gap-1.5 bg-slate-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-slate-600"
                    >
                      <X className="w-3 h-3" /> رفض ({bulk.size})
                    </button>
                  </>
                )}
              </>
            )}

            {approved.length > 0 && (
              <button
                onClick={applyApproved}
                disabled={applying}
                data-testid="apply-images-to-salla-btn"
                className="flex items-center gap-1.5 bg-[#004EEB] text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-blue-700 disabled:opacity-50"
              >
                {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                تطبيق {approved.length} على سلة
              </button>
            )}
          </div>

          {suggestions.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
              <CheckCircle2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 font-medium">لا توجد اقتراحات في هذه الحالة</p>
            </div>
          )}

          <div className="space-y-2">
            {suggestions.map((s) => {
              const isPending = s.status === "pending";
              const isEditing = editing[s.id] !== undefined;
              return (
                <div
                  key={s.id}
                  className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex gap-3"
                  data-testid={`image-suggestion-${s.id}`}
                >
                  {isPending && (
                    <input
                      type="checkbox"
                      checked={bulk.has(s.id)}
                      onChange={() => {
                        setBulk(b => {
                          const n = new Set(b);
                          n.has(s.id) ? n.delete(s.id) : n.add(s.id);
                          return n;
                        });
                      }}
                      className="w-4 h-4 mt-1 accent-[#004EEB] cursor-pointer shrink-0"
                    />
                  )}

                  <img
                    src={s.image_url}
                    alt=""
                    className="w-20 h-20 rounded-xl object-cover border border-slate-200 shrink-0"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-slate-800 truncate">{s.product_name}</p>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        s.status === "pending"  ? "bg-amber-100 text-amber-700" :
                        s.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                        s.status === "applied"  ? "bg-blue-100 text-[#004EEB]" :
                                                  "bg-slate-100 text-slate-500"
                      }`}>
                        {{pending: "معلّق", approved: "معتمد", applied: "مُطبَّق", rejected: "مرفوض"}[s.status]}
                      </span>
                    </div>

                    {s.original_alt && (
                      <p className="text-[11px] text-slate-400 mt-1">
                        <span className="font-semibold">ALT الحالي:</span> {s.original_alt}
                      </p>
                    )}

                    {!isEditing ? (
                      <p className="text-sm text-slate-700 mt-1 leading-relaxed">
                        <span className="font-semibold text-emerald-700">ALT المقترح: </span>
                        {s.suggested_alt}
                      </p>
                    ) : (
                      <textarea
                        value={editing[s.id]}
                        onChange={(e) => setEditing(prev => ({ ...prev, [s.id]: e.target.value }))}
                        rows={2}
                        maxLength={140}
                        className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        data-testid={`edit-image-alt-${s.id}`}
                      />
                    )}

                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <span className="text-[10px] text-slate-400">
                        {s.suggested_alt?.length || 0} حرف
                      </span>
                      {isPending && !isEditing && (
                        <>
                          <button
                            onClick={() => approveOne(s.id)}
                            data-testid={`approve-image-${s.id}`}
                            className="flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[11px] font-semibold px-2 py-1 rounded-full hover:bg-emerald-100"
                          >
                            <Check className="w-3 h-3" /> موافق
                          </button>
                          <button
                            onClick={() => rejectOne(s.id)}
                            data-testid={`reject-image-${s.id}`}
                            className="flex items-center gap-1 bg-slate-50 text-slate-600 text-[11px] font-semibold px-2 py-1 rounded-full hover:bg-slate-100"
                          >
                            <X className="w-3 h-3" /> رفض
                          </button>
                          <button
                            onClick={() => setEditing(prev => ({ ...prev, [s.id]: s.suggested_alt }))}
                            className="flex items-center gap-1 bg-blue-50 text-[#004EEB] text-[11px] font-semibold px-2 py-1 rounded-full hover:bg-blue-100"
                          >
                            <Pencil className="w-3 h-3" /> تعديل
                          </button>
                        </>
                      )}
                      {isEditing && (
                        <>
                          <button
                            onClick={() => saveEdit(s.id)}
                            className="flex items-center gap-1 bg-emerald-600 text-white text-[11px] font-semibold px-2 py-1 rounded-full hover:bg-emerald-700"
                          >
                            <Check className="w-3 h-3" /> حفظ
                          </button>
                          <button
                            onClick={() => setEditing(prev => { const n = { ...prev }; delete n[s.id]; return n; })}
                            className="text-[11px] text-slate-400 hover:text-slate-700 px-2"
                          >
                            إلغاء
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
